import { Link } from 'react-router-dom';
import type { Article, AccentTone } from '@/types/content';
import { getCategory } from '@/data/categories';

export const toneBadge: Record<AccentTone, string> = {
  primary: 'bg-primary-100 text-primary-800',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-900',
};

interface Props {
  article: Article;
  variant?: 'grid' | 'list' | 'compact';
  index?: number;
}

export default function ArticleCard({ article, variant = 'grid', index }: Props) {
  const category = getCategory(article.category);
  const badge = category ? toneBadge[category.accent] : toneBadge.secondary;

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 py-3.5 border-b border-background-200 last:border-0"
      >
        {typeof index === 'number' && (
          <span className="shrink-0 w-6 font-heading font-bold text-lg text-primary-300 group-hover:text-primary-500 transition-colors leading-6">
            {String(index + 1).padStart(2, '0')}
          </span>
        )}
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-foreground-900 leading-6 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </span>
          <span className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
            <span>{category?.name}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span>{article.readingTime} 分钟</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === 'list') {
    return (
      <article className="group grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-5 py-6 border-b border-background-200">
        <Link
          to={`/article/${article.slug}`}
          className="block w-full h-40 sm:h-full sm:min-h-[140px] overflow-hidden rounded-lg bg-background-200"
        >
          <img
            src={article.cover}
            alt={article.title}
            width={1200}
            height={800}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </Link>
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-2">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${badge}`}
              >
                {category.name}
              </Link>
            )}
            <time className="text-xs text-foreground-500" dateTime={article.publishedAt}>
              {article.publishedAt}
            </time>
          </div>
          <h3 className="font-heading text-lg md:text-xl font-semibold leading-snug">
            <Link
              to={`/article/${article.slug}`}
              className="text-foreground-950 hover:text-primary-600 transition-colors"
            >
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 text-sm leading-7 text-foreground-600 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" /> {article.readingTime} 分钟阅读
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors">
      <Link
        to={`/article/${article.slug}`}
        className="block w-full h-48 overflow-hidden bg-background-200"
      >
        <img
          src={article.cover}
          alt={article.title}
          width={1200}
          height={800}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </Link>
      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-center gap-3 mb-3">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${badge}`}
            >
              {category.name}
            </Link>
          )}
          <span className="text-xs text-foreground-500">{article.readingTime} 分钟</span>
        </div>
        <h3 className="font-heading text-base font-semibold leading-snug">
          <Link
            to={`/article/${article.slug}`}
            className="text-foreground-950 hover:text-primary-600 transition-colors line-clamp-2"
          >
            {article.title}
          </Link>
        </h3>
        <p className="mt-2.5 text-sm leading-7 text-foreground-600 line-clamp-3">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs text-foreground-500">
          <span>{article.author}</span>
          <time dateTime={article.publishedAt}>{article.publishedAt}</time>
        </div>
      </div>
    </article>
  );
}